import { useEffect, useRef } from 'react';
import { findAnnouncement } from '../../announcements/announcements';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { postMessageToParent } from '../messaging';

/**
 * Mirrors the "user has unread announcements" Redux flag onto the host page's FAB
 * via postMessage so the launcher shows a red dot even when the assistant panel is
 * closed (#1618). No-op in standalone (non-embedded) mode — postMessageToParent
 * silently skips when window.parent === window.
 */
export const useAnnouncementsBadgeBridge = () => {
    // Count only ids the webapp catalog can actually render. If backend deploys ahead
    // of webapp (catalog drift), the host FAB shouldn't light up for an announcement
    // the user then can't see when they open the panel.
    const eligibleCount = useAppSelector(
        (state: RootState) => state.announcements.eligibleIds.filter((id) => findAnnouncement(id)).length,
    );
    const featureFlagOn = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.AnnouncementsEnabled);
    const lastSent = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        // Also clear the badge when the feature flag is dark, even if the slice still
        // holds eligible ids from a previous session.
        const visible = featureFlagOn && eligibleCount > 0;
        if (lastSent.current === visible) {
            return;
        }
        lastSent.current = visible;
        postMessageToParent({
            event: 'announcementsBadge',
            data: { visible },
        });
    }, [eligibleCount, featureFlagOn]);
};
